import { Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { UpdateBenefitActivationDTO } from 'probonio-shared-ui/api';
import { RadioControl } from 'probonio-shared-ui/component/form';
import { useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DialogLoadingButton } from '../../../component/dialog/DialogLoadingButton';
import { DatePickerControl } from '../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../extension/interface/benefit-activation-dialog.extension.interface';
import { useBenefitContext } from '../BenefitContext';
import { getExtensionForBenefit } from '../index';

interface Props {
  onCancel: () => void;
  onUpdate: (update: UpdateBenefitActivationDTO) => Promise<void>;
  terminationDate?: DateTime;
}

interface FormValues {
  deactivateMode: 'NEAREST_DATE' | 'END_OF_MONTH' | 'CUSTOM';
  endDate?: Date;
}

export const DeactivateForm: React.FC<Props> = ({ onCancel, onUpdate, terminationDate }) => {
  const { t } = useTranslation('benefitsModule');
  const { benefit } = useBenefitContext();

  const maxDate = (terminationDate ? terminationDate.endOf('month') : DateTime.now().plus({ years: 10 })).toJSDate();

  const { control, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { deactivateMode: 'NEAREST_DATE', endDate: new Date() },
  });

  const benefitActivationDialogExtension = getExtensionForBenefit(benefit, BenefitActivationDialogExtensionInterface);

  const handleSave = useCallback(
    async (values: FormValues) => {
      let update: UpdateBenefitActivationDTO;
      if (values.deactivateMode === 'NEAREST_DATE') {
        update = { endAtNearestDate: true };
      } else if (values.deactivateMode === 'END_OF_MONTH') {
        update = { endDate: DateTime.now().toFormat('yyyy-MM') };
      } else {
        update = { endDate: DateTime.fromJSDate(values.endDate!).toFormat('yyyy-MM') };
      }
      await onUpdate(update);
    },
    [onUpdate],
  );

  const deactivateMode = useWatch({ control, name: 'deactivateMode' });
  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <Stack spacing={2}>
        <RadioControl
          control={control}
          name="deactivateMode"
          label={
            <Typography color="text.secondary" component="span">
              <Trans
                i18nKey={[
                  `benefitsModule:${benefit}.bulkEditDialog.deactivate.mode.info`,
                  'benefitsModule:bulkEditDialog.deactivate.mode.info',
                ]}
              />
            </Typography>
          }
          rules={{ required: true }}
          options={[
            {
              testId: 'deactivateMode-nearestDate',
              label: t('bulkEditDialog.deactivate.mode.nearestDate.label'),
              hint: t('bulkEditDialog.deactivate.mode.nearestDate.hint'),
              value: 'NEAREST_DATE',
            },
            {
              testId: 'deactivateMode-endOfMonth',
              label: t('bulkEditDialog.deactivate.mode.endOfMonth.label'),
              hint: t('bulkEditDialog.deactivate.mode.endOfMonth.hint'),
              value: 'END_OF_MONTH',
            },
            {
              testId: 'deactivateMode-custom',
              label: t('bulkEditDialog.deactivate.mode.custom.label'),
              hint: t('bulkEditDialog.deactivate.mode.custom.hint'),
              value: 'CUSTOM',
            },
          ]}
        />
        <DatePickerControl
          monthYearPicker
          control={control}
          name="endDate"
          label={t('bulkEditDialog.deactivate.endDate')}
          disabled={deactivateMode !== 'CUSTOM'}
          rules={{ required: deactivateMode === 'CUSTOM' }}
          minDate={DateTime.now().startOf('month').toJSDate()}
          maxDate={maxDate}
        />
      </Stack>
      {!!benefitActivationDialogExtension?.deactivateActivationHintKey && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t(benefitActivationDialogExtension.deactivateActivationHintKey)}
        </Typography>
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1} mt={3}>
        <DialogLoadingButton type="submit" color="primary" loading={formState.isSubmitting} data-test-id="deactivateForm-confirmButton">
          {t('common:buttons.confirm')}
        </DialogLoadingButton>
      </Stack>
    </form>
  );
};
